<script setup lang="ts">
import { formatFileSize } from '@/components/documents/fileMeta'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import { useDocumentAreas } from '@/pages/investors/documents/useDocumentAreas'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DocumentArea = App.Enums.DocumentArea
import DocumentUploadAreaData = App.Data.Documents.DocumentUploadAreaData

const props = defineProps<{
  uploadAreas: DocumentUploadAreaData[]
}>()

const { t, locale } = useI18n()
const { uploading, upload } = useDocumentUpload()
const { areaLabel } = useDocumentAreas()

const selectableAreas = computed(() => props.uploadAreas.filter((area) => area.targets.length > 0))

const areaOptions = computed(() =>
  selectableAreas.value.map((area) => ({ value: area.area, label: areaLabel(area.area) })),
)

const selectedArea = ref<DocumentArea | null>(selectableAreas.value[0]?.area ?? null)

const area = computed(() => selectableAreas.value.find((entry) => entry.area === selectedArea.value) ?? null)

const targetOptions = computed(() =>
  (area.value?.targets ?? []).map((target) => ({ value: target.id, label: target.label })),
)

const selectedTargetId = ref<string | null>(targetOptions.value[0]?.value ?? null)
const selectedType = ref<string | null>(null)

watch(area, () => {
  selectedTargetId.value = targetOptions.value[0]?.value ?? null
  selectedType.value = null
})

const files = ref<File[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const dropTargetActive = ref(false)

const ready = computed(
  () => files.value.length > 0 && area.value !== null && selectedTargetId.value !== null && selectedType.value !== null,
)

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement

  files.value = [...(input.files ?? [])]
  input.value = ''
}

function onDrop(event: DragEvent): void {
  dropTargetActive.value = false
  files.value = [...(event.dataTransfer?.files ?? [])]
}

async function submit(): Promise<void> {
  if (!ready.value || !area.value) {
    return
  }

  await upload(
    {
      attachableType: area.value.attachableType,
      attachableId: selectedTargetId.value!,
      documentType: selectedType.value,
    },
    files.value,
  )

  files.value = []
  selectedType.value = null
}
</script>

<template>
  <FinestCard :title="t('heading')" icon="pi pi-cloud-upload">
    <p class="text-ink-3 mb-5 text-base">{{ t('hint') }}</p>

    <div class="grid gap-4 lg:grid-cols-3">
      <div>
        <p class="field-label flex items-center gap-2">
          <span
            class="bg-action inline-flex size-[18px] flex-none items-center justify-center rounded-full text-[10px] font-bold text-white">
            1
          </span>
          {{ t('steps.file') }}
        </p>

        <div
          class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-5 text-center transition-colors"
          :class="dropTargetActive ? 'border-action bg-info-subtle' : 'border-line bg-surface-2'"
          @dragover.prevent="dropTargetActive = !uploading"
          @dragleave.prevent="dropTargetActive = false"
          @drop.prevent="onDrop">
          <i class="pi pi-cloud-upload text-ink-3 text-2xl!" aria-hidden="true" />
          <p class="text-ink font-medium">{{ t('drop.title') }}</p>
          <FinestButton variant="ghost" size="sm" :label="t('drop.choose')" @click="fileInput?.click()" />
          <p class="text-ink-3 text-xs">{{ t('drop.formats') }}</p>
        </div>

        <ul v-if="files.length" class="mt-3 space-y-1">
          <li v-for="file in files" :key="file.name" class="border-line flex items-center gap-2 rounded-lg border p-2">
            <i class="pi pi-file text-ink-3 text-xs!" aria-hidden="true" />
            <span class="min-w-0 flex-1 truncate text-sm">{{ file.name }}</span>
            <span class="text-ink-3 flex-none text-xs">{{ formatFileSize(file.size, locale) }}</span>
          </li>
        </ul>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <p class="field-label flex items-center gap-2">
            <span
              class="bg-action inline-flex size-[18px] flex-none items-center justify-center rounded-full text-[10px] font-bold text-white">
              2
            </span>
            {{ t('steps.where') }}
          </p>
          <FinestSelect v-model="selectedArea" :options="areaOptions" :aria-label="t('steps.where')" />
        </div>

        <div>
          <label class="field-label" for="document-upload-target">{{ t('target') }}</label>
          <FinestSelect
            id="document-upload-target"
            v-model="selectedTargetId"
            :options="targetOptions"
            :placeholder="t('targetPlaceholder')" />
          <p class="text-ink-3 mt-1 text-xs">{{ t('targetHelp') }}</p>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <p class="field-label flex items-center gap-2">
            <span
              class="bg-action inline-flex size-[18px] flex-none items-center justify-center rounded-full text-[10px] font-bold text-white">
              3
            </span>
            {{ t('steps.what') }}
          </p>
          <FinestSelect
            v-model="selectedType"
            :options="area?.typeOptions ?? []"
            :placeholder="t('typePlaceholder')"
            :aria-label="t('steps.what')" />
          <p class="text-ink-3 mt-1 text-xs">{{ t('typeHelp') }}</p>
        </div>

        <FinestButton
          class="self-start"
          icon="pi pi-upload"
          :label="t('submit')"
          :disabled="!ready || uploading"
          @click="submit" />
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      @change="onSelect" />
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "heading": "Dokument hochladen",
    "hint": "Datei auswählen, dann festlegen wohin sie gehört und was sie ist.",
    "steps": {
      "file": "Datei",
      "where": "Wohin gehört die Datei?",
      "what": "Was ist die Datei?"
    },
    "drop": {
      "title": "Datei hierher ziehen",
      "choose": "vom Rechner auswählen",
      "formats": "PDF, JPG, PNG, DOCX"
    },
    "target": "Zuordnung",
    "targetPlaceholder": "Auswählen",
    "targetHelp": "Bei Immobilie und Unternehmen steht hier das Objekt, bei Profil und Bonität die Person.",
    "typePlaceholder": "Dokumentart wählen",
    "typeHelp": "Die Auswahl richtet sich nach dem gewählten Bereich.",
    "submit": "Hochladen"
  },
  "en": {
    "heading": "Upload document",
    "hint": "Pick a file, then state where it belongs and what it is.",
    "steps": {
      "file": "File",
      "where": "Where does the file belong?",
      "what": "What is the file?"
    },
    "drop": {
      "title": "Drag a file here",
      "choose": "choose from your computer",
      "formats": "PDF, JPG, PNG, DOCX"
    },
    "target": "Assignment",
    "targetPlaceholder": "Select",
    "targetHelp": "For properties and companies this is the object, for profile and credit the person.",
    "typePlaceholder": "Choose document type",
    "typeHelp": "The choices depend on the selected area.",
    "submit": "Upload"
  }
}
</i18n>
